import {
  DeleteOutlined,
  DownloadOutlined,
  EllipsisOutlined,
  ExclamationCircleOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import { Modal as BaseModal, Button, Dropdown } from 'antd';
import { observer } from 'mobx-react';
import React, { useState } from 'react';
import styled from 'styled-components';

import { hostingStoragePathToUrl } from 'services/Firebase';
import type VideoBundle from 'stores/VideoBundle';
import { estimateVideoItemTaskProgress } from 'stores/VideoBundleElement';
import { last } from 'utils/Array';
import { formatDateTimeLongMinutes } from 'utils/Dates';

import VideoBundleElementRow from './VideoBundleElementRow';

const Container = styled.div`
  align-items: center;
  border-bottom: 1px solid #eeeeee;
  display: flex;
  justify-content: space-between;
  padding: 9px 16px;
  min-height: 5em;
`;

const LabelStack = styled.div`
  display: flex;
  flex-flow: column;
  gap: 2px;
`;

const ButtonsContainer = styled.div`
  display: flex;
  gap: 7px;
`;

const DateLabel = styled.div`
  font-size: 12px;
  flex-grow: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding-right: 10px;
  color: #333;
`;

const MainLabel = styled.div`
  font-weight: bold;
`;

const ProgressLabel = styled.div`
  font-size: 12px;
  color: #333;
  margin-right: 10px;
`;

const ErrorText = styled.div`
  font-size: 12px;
  color: #f00;
`;

const Modal = styled(BaseModal)`
  .ant-modal-body {
    height: 60vh;
    padding: 0;
    overflow: auto;
  }
`;

export interface VideoBundleRowProps {
  videoBundle: VideoBundle;
}

function calculateProgress(videoBundle: VideoBundle) {
  if (!videoBundle.elements.mounted) return 0;

  const itemCount = videoBundle.elements.length;
  if (itemCount <= 0) return 0;

  const sumProgress = videoBundle.elements.all.reduce((acc, el) => {
    return acc + (el.data ? estimateVideoItemTaskProgress(el.data) : 0);
  }, 0);

  return (sumProgress / itemCount) * 100;
}

function renderProgressLabel(videoBundle: VideoBundle) {
  const prefix = 'Progress:\xA0';

  if (videoBundle.data.status === 'complete') {
    return `${prefix}Done`;
  }

  // if we're not prepared to show "Done", then don't show "100%" either
  const progress = calculateProgress(videoBundle) * 0.98;
  if (0 <= progress && progress <= 100) {
    return `${prefix}${progress.toFixed(0)}%`;
  }

  return `${prefix}-`;
}

function VideoBundleRow({ videoBundle }: VideoBundleRowProps) {
  const [isDetailViewOpen, setIsDetailViewOpen] = useState(false);
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);

  const formattedDate = formatDateTimeLongMinutes(videoBundle.data.createTime);
  const bundleError = last(videoBundle.data.errors);

  const downloadZip = () => {
    if (videoBundle.data.archivePath) {
      const url = hostingStoragePathToUrl(videoBundle.data.archivePath);
      window.open(url, '_blank');
    }
  };

  const showDetailView = () => {
    if (!videoBundle.elements.mounted) {
      void videoBundle.elements.load();
    }
    setIsDetailViewOpen(true);
  };

  const dismissDetailView = () => {
    setIsDetailViewOpen(false);
  };

  const handleDelete = async () => {
    await videoBundle.parent?.delete(videoBundle.id);
    setIsConfirmDeleteOpen(false);
  };

  const dismissConfirmDelete = () => {
    setIsConfirmDeleteOpen(false);
  };

  const showConfirmDelete = () => {
    setIsConfirmDeleteOpen(true);
  };

  return (
    <>
      <Container>
        <LabelStack>
          <MainLabel>{videoBundle.data.name}</MainLabel>
          <ProgressLabel>{renderProgressLabel(videoBundle)}</ProgressLabel>
          {bundleError && <ErrorText>•&nbsp;{bundleError}</ErrorText>}
        </LabelStack>

        <ButtonsContainer>
          <DateLabel>{formattedDate}</DateLabel>

          {videoBundle.loading && <LoadingOutlined />}

          {videoBundle.status === 'error' && (
            <ExclamationCircleOutlined style={{ color: 'red' }} />
          )}

          <Button onClick={showDetailView} disabled={videoBundle.loading}>
            Details
          </Button>

          {videoBundle.data.archivePath && (
            <Button
              onClick={downloadZip}
              disabled={videoBundle.loading}
              icon={<DownloadOutlined />}
            />
          )}

          <Dropdown
            trigger={['click']}
            menu={{
              items: [
                {
                  key: 'Delete',
                  label: (
                    <>
                      <DeleteOutlined />
                      &nbsp;Delete
                    </>
                  ),
                  onClick: showConfirmDelete,
                },
              ],
            }}
          >
            <Button>
              <EllipsisOutlined />
            </Button>
          </Dropdown>
        </ButtonsContainer>
      </Container>

      <Modal
        title="Video Bundle Details"
        open={isDetailViewOpen}
        onCancel={dismissDetailView}
        footer={[
          <Button key="close" onClick={dismissDetailView}>
            Close
          </Button>,
        ]}
        width={700}
      >
        {videoBundle.elements.all.map((element) => (
          <VideoBundleElementRow
            key={`${videoBundle.id} ${element.id}`}
            element={element}
          />
        ))}
      </Modal>

      <BaseModal
        title="Delete Video Bundle"
        open={isConfirmDeleteOpen}
        onCancel={dismissConfirmDelete}
        onOk={handleDelete}
        okText="Delete"
        okButtonProps={{ danger: true }}
      >
        <p>
          Are you sure you want to delete this video bundle? This action cannot
          be undone.
        </p>
      </BaseModal>
    </>
  );
}

export default observer(VideoBundleRow);
